import type { Actor, Role } from '@hrms/shared'
import type { DriveStep } from 'driver.js'

export interface TourStep extends DriveStep {
  /** Prefer `[data-tour="..."]` selectors so tours survive markup changes. */
  element?: string
  /**
   * Runs before the step shows (open a modal, switch a tab, open a ticket). The step waits
   * briefly for its element afterwards and is skipped if the element never appears.
   */
  prepare?: () => void | Promise<void>
}

export interface TourDefinition {
  id: string
  title: string
  /** One line for the "Take a tour" menu. */
  summary: string
  /** Roles allowed to see the tour; omit for everyone. */
  roles?: readonly Role[]
  /** Finer check than `roles` (permissions). */
  available?: (user: Actor) => boolean
  /** The page the tour runs on; starting it navigates there first. */
  path?: string
  /** Run automatically the first time a matching user lands on `path` (or anywhere). */
  autoStart?: boolean
  /** Lower runs first when several could auto-start. */
  order?: number
  steps: TourStep[] | ((user: Actor) => TourStep[])
  /** Cleanup when the tour ends (e.g. close a modal it opened). */
  onExit?: () => void
}

const tours = new Map<string, TourDefinition>()

export function registerTour(tour: TourDefinition): void {
  if (tours.has(tour.id)) console.warn(`Tour "${tour.id}" registered twice; replacing.`)
  tours.set(tour.id, tour)
}

export function getTour(id: string): TourDefinition | undefined {
  return tours.get(id)
}

export function listTours(): TourDefinition[] {
  return [...tours.values()].sort((a, b) => (a.order ?? 50) - (b.order ?? 50))
}

export function tourAvailable(tour: TourDefinition, user: Actor): boolean {
  if (tour.roles && tour.roles.length > 0 && !tour.roles.includes(user.role)) return false
  return tour.available ? tour.available(user) : true
}

/** `[data-tour="name"]` selector helper. */
export const tourTarget = (name: string) => `[data-tour="${name}"]`

/** Visible on screen (present, laid out, not `display:none` / inside a hidden drawer). */
export function isOnScreen(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false
  if (el.getClientRects().length === 0) return false
  return !el.closest('[inert]')
}

/** Resolves with the first on-screen match within `timeout` ms, or null. */
export function waitForElement(selector: string, timeout = 1500): Promise<HTMLElement | null> {
  const find = () =>
    [...document.querySelectorAll(selector)].find((el) => isOnScreen(el)) as HTMLElement | undefined
  return new Promise((resolve) => {
    const start = performance.now()
    const tick = () => {
      const el = find()
      if (el) return resolve(el)
      if (performance.now() - start >= timeout) return resolve(null)
      window.setTimeout(tick, 60)
    }
    tick()
  })
}

/** Clicks the first on-screen element matching `selector` (e.g. open the first ticket). */
export async function clickFirst(selector: string): Promise<boolean> {
  const el = await waitForElement(selector, 1200)
  el?.click()
  return !!el
}
